<script setup>
import useVuelidate from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import { reactive, watch } from 'vue'

import InputText from '@/components/form/InputText.vue'
import FunnelStep from '@/components/FunnelStep.vue'
import { useFormDataStore } from '@/stores/FormDataStore'
import { useUserStore } from '@/stores/UserStore'
import { useHelpers } from '@/useHelpers'

const formDataStore = useFormDataStore()
const userStore = useUserStore()

const { sendMessage, visitUrl } = useHelpers()

defineProps({
  id: {
    type: Number,
    required: true
  },
  name: {
    type: String,
    required: true
  },
  isActive: {
    type: Boolean,
    required: true
  },
  isCompleted: {
    type: Boolean,
    required: true
  }
})

const state = reactive({
  error: '',
  form: {
    email: '@'
  },
  isFormSubmitting: false
})

const validation = {
  email: {
    required,
    email,
  }
}

const $vLogin = useVuelidate(validation, state.form, { $stopPropagation: true })

const emits = defineEmits(['onClickCompleteStep', 'onChangeClicked'])

const handleLogOut = () => {
  if (userStore.isLoggedIn) {
    visitUrl(window.api.logout.url)
  } else {
    emits('onChangeClicked')
  }
}

const handleSsoLogin = (provider) => {
  sendMessage('triggerLogin', {
    email: '',
    provider,
    selectedSubscription: formDataStore.selectedSubscription.code
  })
}

const handleContinue = () => {
  $vLogin.value.$touch()
  if ($vLogin.value.$anyDirty && !$vLogin.value.$error) {
    sendMessage('triggerLogin', {
      email: state.form.email,
      selectedSubscription: formDataStore.selectedSubscription.code
    })
  }
}

watch(
  () => state.form.email,
  () => userStore.reset()
)
</script>

<template>
  <FunnelStep
    :id="id"
    :name="name"
    :details="$t('Account')"
    :is-active="isActive"
    :is-completed="isCompleted"
    @on-change-clicked="handleLogOut"
  >
    <p class="step-content__description">
      {{ $t('Log in or register to continue shopping. Enter your email or use the option to sign in with Google or Facebook.') }}
    </p>

    <div class="step-account__form">
      <div class="input-group">
        <div class="input-label">{{ $t('E-mail') }}</div>
        <InputText
          v-model="state.form.email"
          type="email"
          :validator="$vLogin.email"
          :error="state.error"
          :placeholder="$t('E-mail placeholder')"
          @keyup.enter="handleContinue"
        />
      </div>

      <div class="input-group button-group">
        <button
          @click="handleContinue"
          :disabled="state.isFormSubmitting"
          class="button button--large button--primary step-account__button"
        >
          {{ $t('Continue') }}
        </button>
      </div>
    </div>

    <div class="separator">
      <span class="separator__text">{{ $t('Or') }}</span>

      <div class="separator__line"></div>
    </div>

    <a
      id="connect-google"
      @click="handleSsoLogin('google')"
      href="#"
      rel="noopener noreferrer"
      class="button button--secondary button--large button--google button--full-width"
    >
      {{ $t('Sign in with Google') }}
    </a>

    <a
      href="#"
      @click="handleSsoLogin('facebook')"
      rel="noopener noreferrer"
      class="button button--secondary button--large button--facebook button--full-width"
    >
      {{ $t('Sign in with Facebook') }}
    </a>
  </FunnelStep>
</template>
